// SceneTypography — chapter copy as part of the composition, never a card.
// Each chapter gets its own placement and phrase. Re-mounting on scene changes
// lets the words arrive with the shot instead of sitting in the UI chrome.
//
// The words are split into letters so each glyph rises out of a masked
// line with its own delay; a giant outlined "ghost" chapter numeral sits
// behind the composition; the whole word block skews with scroll
// velocity (--scroll-velocity, written by useScrollTimeline). The
// projects chapter additionally gets a marquee strip of shipped work.

import type { CSSProperties } from 'react';
import { SATELLITES, type SceneConfig } from './planetSections';

interface ScenePhrase {
  words: string[];
  caption: string;
}

const PHRASES: Partial<Record<string, ScenePhrase>> = {
  about: {
    words: ['SMALL TOOLS', 'SLOW IDEAS'],
    caption: 'A personal corner of the internet, assembled one careful system at a time.',
  },
  skills: {
    words: ['CODE HAS', 'A MATERIAL'],
    caption: 'Type, motion, models, and the small choices that make a thing feel alive.',
  },
  projects: {
    words: ['THINGS I', 'SENT OUT'],
    caption: 'Projects with their own gravity, released into the world and kept in motion.',
  },
  notes: {
    words: ['WORK LEFT', 'OPEN'],
    caption: 'Notes from the middle of making: decisions, reversals, and details worth keeping.',
  },
  timeline: {
    words: ['SEND A', 'SIGNAL'],
    caption: 'The line is open for collaborations, strange ideas, and useful work.',
  },
  contact: {
    words: ['NO STRAIGHT', 'LINES HERE'],
    caption: 'A path made from experiments, detours, and the things that survived them.',
  },
};

/** Split a word into per-letter spans with a staggered rise delay. */
export function Letters({ text, base = 0 }: { text: string; base?: number }) {
  return (
    <>
      {Array.from(text).map((ch, i) => (
        <span
          key={`${i}-${ch}`}
          className="scene-typography__letter"
          style={{ '--letter-order': base + i } as CSSProperties}
        >
          {ch === ' ' ? '\u00A0' : ch}
        </span>
      ))}
    </>
  );
}

export interface SceneTypographyProps {
  scene: SceneConfig;
  visible: boolean;
}

export function SceneTypography({ scene, visible }: SceneTypographyProps) {
  const phrase = PHRASES[scene.key];
  if (!phrase || !visible) return null;

  const marqueeRun =
    scene.key === 'projects'
      ? SATELLITES.map((s) => `${s.name} · ${s.year}`)
      : null;

  return (
    <section
      key={scene.key}
      className={`scene-typography scene-typography--${scene.key}`}
      aria-hidden="true"
    >
      <span className="scene-typography__ghost">
        {String(scene.id).padStart(2, '0')}
      </span>
      <div className="scene-typography__words">
        {phrase.words.map((word, index) => (
          <span
            key={word}
            className="scene-typography__word"
            style={{ '--word-order': index } as CSSProperties}
          >
            <span className="scene-typography__word-inner">
              <Letters text={word} base={index * 5} />
            </span>
          </span>
        ))}
      </div>
      <p className="scene-typography__caption">{phrase.caption}</p>
      {marqueeRun && (
        <div className="scene-typography__marquee">
          <div className="scene-typography__marquee-track">
            {[0, 1].map((dup) => (
              <span key={dup} className="scene-typography__marquee-run">
                {marqueeRun.map((item) => (
                  <span key={item} className="scene-typography__marquee-item">
                    {item}
                  </span>
                ))}
              </span>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

export default SceneTypography;
